/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dimensions, TouchableOpacity, View } from "react-native";
import AgoraRTC, {
  AgoraVideoPlayer,
  createClient,
  createMicrophoneAndCameraTracks,
  ClientConfig,
  IAgoraRTCRemoteUser,
  IMicrophoneAudioTrack,
  ICameraVideoTrack,
} from "agora-rtc-react";
import {
  AIDenoiserExtension,
  AIDenoiserProcessor,
  AIDenoiserProcessorLevel,
  AIDenoiserProcessorMode,
} from "agora-extension-ai-denoiser";
import SwipeableViews from "react-swipeable-views";
import Icon from "react-icons-kit";
import { ic_info_outline } from "react-icons-kit/md/ic_info_outline";
import { settings } from "react-icons-kit/feather/settings";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState, SelectedSkillObjectType } from "store/types";

import {
  DeclareMatchReadyResponse,
  DeclareMatchStepReadyResponse,
  DeclareMatchCancelResponse,
  DeclareMatchEndMutation,
  GetUserSessionListQuotaCompletionQuery,
  GetUserSummitSessionListQuery,
  CancelReasonEnum,
  SendKudosResponse,
} from "graphql/internal/generated";
import {
  GET_USER_SESSION_QUOTA_COMPLETION,
  GET_USER_SUMMIT_SESSION_LIST,
} from "graphql/internal/Queries";
import {
  DECLARE_MATCH_READY,
  DECLARE_MATCH_STEP_READY,
  DECLARE_MATCH_CANCEL,
  DECLARE_MATCH_END,
  SEND_KUDOS,
} from "graphql/internal/Mutations";
import {
  Button,
  FadeInOutView,
  IssueModal,
  Text,
  useTimerRef,
  invalidateQueries,
} from "UpduoLibrary";
import { formattedTimeString, getNodes } from "config/Utilities";
import {
  skaleBackgroundBlue,
  skaleBlack,
  skaleBackgroundWhite,
  skaleGreen,
  skaleOrange,
  skalePrimaryBlue,
  skaleWhite,
  skaleOffWhite,
  skaleSoftRed,
  skaleLightGray,
  skaleWebFillLightGray,
  colorWithOpacity,
} from "config/UpduoColors";
import MainOverlay from "./MainOverlay";
import StepCard from "./StepCard";
import StepDetailModal from "./StepDetailModal";
import AgendaModal from "./AgendaModal";
import EndChatModal from "./EndChatModal";
import SkillModal from "./SkillModal";
import SessionProceedModal from "./SessionProceedModal";
import TechnicalSupportModal from "./TechnicalSupportModal";
import ChatSettingsModal from "./ChatSettingsModal";
import AgendaBackModal from "./AgendaBackModal";
import {
  addPubNubSubscribedMessage,
  clearPubNubSubscribedMessages,
} from "config/UpduoPubNub";
import { UpduoSounds } from "config/UpduoSounds";
import { homeResetState } from "config/ResetState";
import { manuallyInvalidateQueries } from "UpduoLibrary/apolloCache";
import { callApi } from "config/UpduoService";

import * as UpduoAnalytics from "config/UpduoAnalytics";

const config: ClientConfig = {
  mode: "live",
  codec: "vp8",
};
const useClient = createClient(config);
const useMicrophoneAndCameraTracks = createMicrophoneAndCameraTracks(
  { AEC: true, ANS: true },
  { optimizationMode: "motion" }
);

const denoiser = new AIDenoiserExtension({ assetsPath: "./external" });

export default function Chat() {
  const history = useHistory();

  const dispatch = useDispatch();

  const displayedSummitView = useSelector(
    (state: RootState) => state.displayedSummitView
  );

  const chatAgendaModalVisible = useSelector(
    (state: RootState) => state.chatAgendaModalVisible
  );

  const chatBeginButtonPressed = useSelector(
    (state: RootState) => state.chatBeginButtonPressed
  );
  const chatBeginButtonPressedRef = useRef<boolean>();
  chatBeginButtonPressedRef.current = chatBeginButtonPressed;

  const isSelfChatReady = useSelector(
    (state: RootState) => state.isSelfChatReady
  );
  const isPartnerChatReady = useSelector(
    (state: RootState) => state.isPartnerChatReady
  );
  const skillObject = useSelector(
    (state: RootState) => state.skillObject
  ) as SelectedSkillObjectType; // TODO handle the uninitialized case

  const selfObject = useSelector((state: RootState) => state.selfObject);

  const matchObject = useSelector((state: RootState) => state.matchObject);
  const skaleSessionObject = useSelector(
    (state: RootState) => state.skaleSessionObject
  );
  const partnerObject = useSelector((state: RootState) => state.partnerObject);

  const lastCompletedSummit = useSelector(
    (state: RootState) => state.lastCompletedSummit
  );

  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );

  const summitSkillList = useSelector(
    (state: RootState) => state.summitSkillList
  );

  const [cancelAlertVisible, setCancelAlertVisible] = useState(false);
  const [stagedMatchCancel, setStagedMatchCancel] = useState(false);
  const [beginChatButtonText, setBeginChatButtonText] =
    useState("Connecting...");

  const [beginChatButtonDisabled, setBeginChatButtonDisabled] = useState(true);
  const beginChatButtonDisabledRef = useRef<boolean>();
  beginChatButtonDisabledRef.current = beginChatButtonDisabled;

  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [delayButton, setDelayButton] = useState(false);
  const [endChatModalVisible, setEndChatModalVisible] = useState(false);
  const [showSkillModalVisible, setShowSkillModalVisible] = useState(false);
  const [sessionSuccess, setSessionSuccess] = useState<{
    skalesessionId: string;
    isCancelled: boolean;
  } | null>(null);

  const [sessionProceedModalVisible, setSessionProceedModalVisible] =
    useState(false);
  const sessionProceedModalVisibleRef = useRef<boolean>();
  sessionProceedModalVisibleRef.current = sessionProceedModalVisible;

  const [publishVideo, setPublishVideo] = useState(true);
  const [videoDisabledAlertVisible, setVideoDisabledAlertVisible] =
    useState(false);
  const [partnerDisconnectedAlertVisible, setPartnerDisconnectedAlertVisible] =
    useState(false);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedSecondsRef = useRef(0);
  elapsedSecondsRef.current = elapsedSeconds;

  const [stepElapsedSeconds, setStepElapsedSeconds] = useState(0);
  const stepElapsedSecondsRef = useRef(0);
  stepElapsedSecondsRef.current = stepElapsedSeconds;

  const [userSummitSessionList, setUserSummitSessionList] = useState<any[]>([]);

  const [kudosSelectedValue, setKudosSelectedValue] = useState({
    Friendly: false,
    "Good conversation": false,
    "Helpful tips": false,
    Knowledgeable: false,
  });
  const [kudosMessageText, setKudosMessageText] = useState("");

  // Restore session steps state, if applicable
  let startingSteps = 1;
  let startingPartnerIsReadyToProceed = false;
  let startingSelfIsReadyToProceed = false;
  if (
    skaleSessionObject.stepsReadySelf &&
    skaleSessionObject.stepsReadyPartner
  ) {
    const stepsReadySelfLength =
      skaleSessionObject?.stepsReadySelf?.length || 0;
    const stepsReadyPartnerLength =
      skaleSessionObject?.stepsReadyPartner?.length || 0;
    if (stepsReadySelfLength > stepsReadyPartnerLength) {
      startingSteps = stepsReadySelfLength;
      startingSelfIsReadyToProceed = true;
    } else if (stepsReadySelfLength < stepsReadyPartnerLength) {
      startingSteps = stepsReadyPartnerLength;
      startingPartnerIsReadyToProceed = true;
    } else {
      startingSteps = stepsReadySelfLength + 1;
    }
  }

  const lastCardStep = skillObject?.sessionSteps?.length || 4;
  const kudosCardStep = lastCardStep + 1;

  const [currentStep, setCurrentStep] = useState(Math.min(startingSteps, 4));
  const currentStepRef = useRef(1);
  currentStepRef.current = currentStep;

  const [showStepDetailModal, setShowStepDetailModal] = useState(false);
  const [showTechnicalSupportModal, setShowTechnicalSupportModal] =
    useState(false);
  const [isChatSettingsModalVisible, setIsChatSettingsModalVisible] =
    useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showLeaveButton, setShowLeaveButton] = useState(true);
  const [isTroubleshootingMenuVisible, setIsTroubleshootingMenuVisible] =
    useState(false);

  const [partnerIsReadyToProceed, setPartnerIsReadyToProceed] = useState(
    startingPartnerIsReadyToProceed
  );
  const [selfIsReadyToProceed, setSelfIsReadyToProceed] = useState(
    startingSelfIsReadyToProceed
  );
  const [matchStepProceedObject, setMatchStepProceedObject] = useState<{
    step?: number;
    recovered?: boolean;
  }>({});

  const [agoraRemoteUsers, setAgoraRemoteUsers] = useState<
    IAgoraRTCRemoteUser[]
  >([]);

  const [hasSelfJoinedChannel, setHasSelfJoinedChannel] = useState(false);
  const [hasPartnerJoinedChannel, setHasPartnerJoinedChannel] = useState(false);

  const [chatEnded, setChatEnded] = useState(false);

  const [completedQuotaSessionsCount, setCompletedQuotaSessionsCount] =
    useState<number | null>(null);

  const [setChatFailureTimer, clearChatFailureTimer] = useTimerRef();
  const [setMatchReadyTimer, clearMatchReadyTimer] = useTimerRef();
  const [setMatchProceedTimer, clearMatchProceedTimer] = useTimerRef();
  const [setElapsedSecondsTimer, clearElapsedSecondsTimer] = useTimerRef();

  const agoraClient = useClient();
  const { ready, tracks } = useMicrophoneAndCameraTracks();
  const readyRef = useRef<boolean>();
  readyRef.current = ready;
  const tracksRef = useRef<[IMicrophoneAudioTrack, ICameraVideoTrack] | null>();
  tracksRef.current = tracks;

  const isLearner = selfObject.userId === skaleSessionObject.learnerId;

  const [
    getCompletedQuotaSessionsCount,
    { loading: completedQuotaSessionsCountLoading },
  ] = useLazyQuery<GetUserSessionListQuotaCompletionQuery>(
    GET_USER_SESSION_QUOTA_COMPLETION,
    {
      fetchPolicy: "network-only",
      onCompleted: (result) => {
        if (result?.userSessions) {
          setCompletedQuotaSessionsCount(
            result.userSessions?.completedCount || null
          );
        }
      },
      onError: (error) => {
        console.error("getCompletedQuotaSessionsCount error:", error);
        UpduoAnalytics.logEvent(
          "app-chat-getcompletedquotasessionscount-error",
          {
            error,
          }
        );
      },
    }
  );

  const _handleLoadCompletedQuotaSessionsCount = useCallback(() => {
    if (selfObject.organizationActiveQuota) {
      getCompletedQuotaSessionsCount({
        variables: {
          getUserSessionListRequest: {
            startDate: selfObject.organizationActiveQuota?.startDate,
            endDate: selfObject.organizationActiveQuota?.endDate,
          },
        },
      });
    } else {
      setCompletedQuotaSessionsCount(null);
    }
  }, [getCompletedQuotaSessionsCount, selfObject.organizationActiveQuota]);

  const [getUserSummitSessionList, {}] =
    useLazyQuery<GetUserSummitSessionListQuery>(GET_USER_SUMMIT_SESSION_LIST, {
      fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
      onCompleted: ({ skalesessions }) => {
        if (skalesessions) {
          const newUserSummitSessionList = getNodes(skalesessions);
          setUserSummitSessionList(newUserSummitSessionList);
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-summit-chat-getusersummitsessionlist-error",
          { error }
        );
      },
    });

  const [declareMatchReady, {}] = useMutation<DeclareMatchReadyResponse>(
    DECLARE_MATCH_READY,
    {
      onCompleted: () => {
        UpduoAnalytics.logEvent("app-initial-agenda-close", {
          skalesessionId: skaleSessionObject.id,
        });
        setBeginChatButtonText("Waiting for partner...");
        dispatch(ActionCreators.setChatBeginButtonPressed(true));
        dispatch(ActionCreators.setIsSelfChatReady(true));
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-initial-agenda-close-error", {
          error,
          skalesessionId: skaleSessionObject.id,
        });
        setBeginChatButtonDisabled(false);
        setBeginChatButtonText("Ready");
      },
    }
  );

  const [declareMatchStepReady, {}] =
    useMutation<DeclareMatchStepReadyResponse>(DECLARE_MATCH_STEP_READY, {
      onCompleted: () => {
        UpduoAnalytics.logEvent("user-step-ready", {
          skalesessionId: skaleSessionObject.id,
          step: currentStepRef.current,
          elapsedSeconds: elapsedSecondsRef.current,
          stepElapsedSeconds: stepElapsedSecondsRef.current,
        });
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "user-step-ready-error",
          Object.assign(
            { error },
            {
              skalesessionId: skaleSessionObject.id,
              step: currentStepRef.current,
              elapsedSeconds: elapsedSecondsRef.current,
              stepElapsedSeconds: stepElapsedSecondsRef.current,
            }
          )
        );
        setSelfIsReadyToProceed(false);
      },
    });

  const [declareMatchCancel, {}] = useMutation<DeclareMatchCancelResponse>(
    DECLARE_MATCH_CANCEL,
    {
      onCompleted: () => {
        _handleMatchCancel(true, "declareMatchCancel onCompleted");
      },
      onError: (error) => {
        _handleMatchCancel(
          true,
          `declareMatchCancel onError ${JSON.stringify(error)}`
        );
      },
    }
  );

  const [declareMatchEnd, { loading: declareMatchEndLoading }] =
    useMutation<DeclareMatchEndMutation>(DECLARE_MATCH_END, {
      onCompleted: ({ declareMatchEnd: result }) => {
        UpduoAnalytics.logEvent("app-match-end", {
          skalesessionId: skaleSessionObject.id,
          duration: elapsedSecondsRef.current,
        });
        if (!result?.ok) {
          throw result;
        }
        setSessionSuccess({
          skalesessionId: skaleSessionObject.id,
          isCancelled: result?.isCancelled || false,
        });
        _handleLoadCompletedQuotaSessionsCount();
        _handleSnapToSendKudos();
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-match-end-error",
          Object.assign(
            { error },
            {
              skalesessionId: skaleSessionObject.id,
              duration: elapsedSecondsRef.current,
            }
          )
        );
      },
    });

  const sendKudosAnalytics = (
    event: string,
    error?: Record<string, string>
  ) => {
    const kudosSelectedNum = Object.values(kudosSelectedValue).filter(
      (value) => value
    ).length;
    UpduoAnalytics.logEvent(event, {
      skalesessionId: skaleSessionObject.id,
      kudosSelectedNum: kudosSelectedNum,
      categoryFriendly: kudosSelectedValue.Friendly,
      categoryGoodConversation: kudosSelectedValue["Good conversation"],
      categoryHelpfulTips: kudosSelectedValue["Helpful tips"],
      categoryKnowledgeable: kudosSelectedValue.Knowledgeable,
      message: kudosMessageText,
      error,
    });
  };

  const [sendKudos, {}] = useMutation<SendKudosResponse>(SEND_KUDOS, {
    onCompleted: () => {
      sendKudosAnalytics("app-sendkudos-success");
      setButtonsDisabled(true);
      _handleShowSessionProceedModal();
    },
    onError: (error) => {
      console.error("ERROR reportIssue", error);
      sendKudosAnalytics("app-sendkudos-failure", {
        message: error.message,
        name: error.name,
      });
      setButtonsDisabled(true);
      _handleShowSessionProceedModal();
    },
    update: invalidateQueries(["kudos", "kudosCounts"]),
  });

  const _handleInitAgoraRtcEngine = async () => {
    try {
      AgoraRTC.enableLogUpload();
      AgoraRTC.setLogLevel(1);
      await agoraClient.setClientRole("host");
      let processor: AIDenoiserProcessor;
      if (denoiser.checkCompatibility()) {
        AgoraRTC.registerExtensions([denoiser]);
        denoiser.onloaderror = (e) => {
          console.log("denoiser.onloaderror", e);
        };
        processor = denoiser.createProcessor();
        processor.onoverload = async (elapsedTime) => {
          console.log("overload!!!", elapsedTime);
          // Switch from AI noise suppression to stationary noise suppression
          await processor.setMode(AIDenoiserProcessorMode.STATIONARY_NS);
          // Disable AI noise suppression
          await processor.disable();
        };
        console.log("Agora AI Denoiser processor created");
      } else {
        // The extension might not be supported in the current browser. You can stop executing further code logic
        console.error("Does not support Agora AI Denoiser");
      }
      if (selfObject.organizationDoAgoraForceTcp) {
        agoraClient.startProxyServer(5); // Force TCP Cloud Proxy mode
      } else if (
        // Specific UDP mode for UScellular organization
        selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTozOTI="
      ) {
        agoraClient.startProxyServer(3); // Force UDP Cloud Proxy mode
      }
      agoraClient.on("connection-state-change", (state) => {
        console.log("event connection-state-change", state);
        if (state === "CONNECTED") {
          setHasSelfJoinedChannel(true);
          setPartnerDisconnectedAlertVisible(false);
          setVideoDisabledAlertVisible(false);
          setBeginChatButtonText("Ready");
          setBeginChatButtonDisabled(false);
        }
      });
      agoraClient.on("user-published", (user, mediaType) => {
        console.log("event user-published", user, mediaType);
        const subscribe = async () => {
          await agoraClient.subscribe(user, mediaType);
          if (mediaType === "video") {
            setAgoraRemoteUsers((prevUsers) => {
              return [...prevUsers, user];
            });
            setHasPartnerJoinedChannel(true);
          }
          if (mediaType === "audio") {
            if (processor) {
              // Use AI denoiser for remote audio
              user.audioTrack
                ?.pipe(processor)
                .pipe(user.audioTrack.processorDestination);
              await processor.setLevel(AIDenoiserProcessorLevel.SOFT);
              await processor.setMode(AIDenoiserProcessorMode.NSNG);
              await processor.enable();
              console.log("Agora AI Denoiser processor enabled");
            }
            user.audioTrack?.play();
          }
        };
        subscribe();
      });
      agoraClient.on("user-unpublished", (user, type) => {
        console.log("event user-unpublished", user, type);
        if (type === "video") {
          setAgoraRemoteUsers((prevUsers) => {
            return prevUsers.filter((u) => u.uid !== user.uid);
          });
        }
        if (type === "audio") {
          user.audioTrack?.stop();
        }
      });
      agoraClient.on("user-left", (user) => {
        console.log("event user-left", user);
        setAgoraRemoteUsers((prevUsers) => {
          return prevUsers.filter((u) => u.uid !== user.uid);
        });
      });
      agoraClient.on("is-using-cloud-proxy", () => {
        console.log("is-using-cloud-proxy");
      });
      await agoraClient.join(
        process.env.REACT_APP_AGORA_APP_ID || "",
        matchObject.agoraChannel,
        matchObject.agoraToken,
        parseInt(atob(selfObject.userId).split(":")[1])
      );
    } catch (error) {
      console.log("_handleInitAgoraRtcEngine error", error);
    }
  };

  const _handlePublishTrack = async (type: "audio" | "video") => {
    // User can only publish after channel is already joined,
    // and tracks are available and ready
    if (readyRef.current && tracksRef.current && hasSelfJoinedChannel) {
      const trackIndex = type === "audio" ? 0 : 1;
      await tracksRef.current[trackIndex].setMuted(false);
      await agoraClient.publish(tracksRef.current[trackIndex]);
    }
  };

  // TODO: Add Agora event handlers

  useEffect(() => {
    dispatch(
      ActionCreators.setIsSelfChatReady(
        Boolean(
          isLearner
            ? skaleSessionObject.isLearnerReady
            : skaleSessionObject.isTeacherReady
        )
      )
    );
    dispatch(
      ActionCreators.setIsPartnerChatReady(
        Boolean(
          isLearner
            ? skaleSessionObject.isTeacherReady
            : skaleSessionObject.isLearnerReady
        )
      )
    );
    dispatch(ActionCreators.setMatchAck(false));
    getUserSummitSessionList({
      variables: {
        getUserSummitSessionListRequest: {
          summitId: selectedSummit?.id || skaleSessionObject?.summitId,
        },
      },
    });
    document.body.style.backgroundColor = skaleWhite;
    _handleInitAgoraRtcEngine();
    return () => {
      document.body.style.backgroundColor = skaleWebFillLightGray;
      _handleCloseChat();
    };
  }, []); // We don't want to call this again, even if selectedSummit or skaleSessionObject change

  useEffect(() => {
    // Trigger the following only when both users have joined channel for the first time
    if (hasSelfJoinedChannel && hasPartnerJoinedChannel) {
      clearChatFailureTimer();
    }
  }, [hasSelfJoinedChannel, hasPartnerJoinedChannel]);

  useEffect(() => {
    // As soon as tracks are available and ready, start publishing video
    if (readyRef.current && tracksRef.current && hasSelfJoinedChannel) {
      _handlePublishTrack("video");
    }
  }, [readyRef.current, tracksRef.current, hasSelfJoinedChannel]);

  useEffect(() => {
    if (displayedSummitView === "Chat") {
      setChatFailureTimer(
        setTimeout(() => {
          if (
            !chatBeginButtonPressedRef.current &&
            beginChatButtonDisabledRef.current
          ) {
            // 60-second window for Agora connection to be established;
            // if still on AgendaModal, return user to HomeScreen
            _handleAgoraGraceExpired();
          }
        }, 60000)
      );
      _handlePrepareForChat();
    }
  }, [
    selfObject.organizationName,
    skaleSessionObject.stepsReadyPartner,
    skaleSessionObject.stepsReadySelf,
  ]);

  useEffect(() => {
    // Ensure that step cards are perfectly synchronized between the two peers
    if (matchStepProceedObject.step) {
      UpduoAnalytics.logEvent("app-step-proceed", {
        skalesessionId: skaleSessionObject.id,
        step: matchStepProceedObject.step,
        stepElapsedSeconds,
        convoId: skillObject.id,
        convoName: skillObject.name,
        stepSuggestedMinutes:
          skillObject.sessionSteps[matchStepProceedObject.step - 1]["minutes"],
        stepHasDetail:
          skillObject.sessionSteps[matchStepProceedObject.step - 1][
            isLearner ? "learner" : "teacher"
          ]["detail"] !== "",
        role: isLearner ? "learner" : "teacher",
        recovered: matchStepProceedObject.recovered,
      });
      //delay button for 1 second before user can press it for each step
      setButtonsDisabled(true);
      setDelayButton(true);
      setTimeout(() => {
        setButtonsDisabled(false);
        setDelayButton(false);
      }, 1000);
      setStepElapsedSeconds(0);
      if (currentStep <= matchStepProceedObject.step) {
        UpduoSounds.proceed.play();
      }
      // Hide step detail when both users have moved on to the next step
      setShowStepDetailModal(false);
      setCurrentStep(matchStepProceedObject.step + 1);
      clearMatchProceedTimer();
    }
  }, [matchStepProceedObject]);

  useEffect(() => {
    if (chatBeginButtonPressed) {
      setMatchReadyTimer(
        setInterval(() => {
          callApi("postSessionStatePoll", { step: 0 }, (responseJson) => {
            if (responseJson?.["are_both_ready"]) {
              dispatch(ActionCreators.setIsPartnerChatReady(true));
              clearMatchReadyTimer();
            }
          });
        }, 5000)
      );
    }
  }, [chatBeginButtonPressed]);

  useEffect(() => {
    if (selfIsReadyToProceed) {
      setMatchProceedTimer(
        setInterval(() => {
          callApi(
            "postSessionStatePoll",
            { step: currentStepRef.current },
            (responseJson) => {
              if ((responseJson?.step || 0) >= currentStepRef.current) {
                const remoteMessage = Object.assign(
                  { recovered: true },
                  responseJson
                );
                _handleMatchStepProceed(remoteMessage);
              }
            }
          );
        }, 5000)
      );
    }
  }, [selfIsReadyToProceed]);

  useEffect(() => {
    if (isSelfChatReady && isPartnerChatReady) {
      // This causes chatAgendaModalVisible to be dismissed,
      // which triggers the useEffect right after this one
      // (which in turn sets up the official start of the session)
      setTimeout(() => {
        _handleDismissModal();
      }, 5000);
    }
  }, [isSelfChatReady, isPartnerChatReady]);

  useEffect(() => {
    if (!chatAgendaModalVisible) {
      UpduoAnalytics.logEvent("app-chat-ready", {
        selfReady: isSelfChatReady,
        partnerReady: isPartnerChatReady,
      });
      dispatch(ActionCreators.setChatConnected(true));
      setElapsedSecondsTimer(
        setInterval(() => {
          setElapsedSeconds((previousState) => previousState + 1);
          setStepElapsedSeconds((previousState) => previousState + 1);
        }, 1000)
      );
      clearMatchReadyTimer();
    }
  }, [chatAgendaModalVisible]);

  useEffect(() => {
    // Disable initial agenda modal and turn audio on
    if (
      hasSelfJoinedChannel &&
      hasPartnerJoinedChannel &&
      !chatAgendaModalVisible &&
      readyRef.current &&
      tracksRef.current &&
      hasSelfJoinedChannel
    ) {
      _handlePublishTrack("audio");
    }
  }, [
    hasSelfJoinedChannel,
    hasPartnerJoinedChannel,
    chatAgendaModalVisible,
    readyRef.current,
    tracksRef.current,
    hasSelfJoinedChannel,
  ]);

  const hasCompletedAllSkillsInSummit = () => {
    if (!summitSkillList?.length || !userSummitSessionList?.length) {
      return false;
    }
    const completedSkillIds = new Set(
      userSummitSessionList.map((session) => session.skill.id)
    );
    for (const skill of summitSkillList) {
      if (!completedSkillIds.has(skill.id)) {
        return false;
      }
    }
    return true;
  };

  const sessionProceedModalLoading =
    declareMatchEndLoading || completedQuotaSessionsCountLoading;

  useEffect(() => {
    // Whenever we are done loading end-of-match requests, check if the user has completed the
    // last required session (either via quota or the fallback of completing a summit's skills).
    // If justCompletedQuota is already true, we keep it true.
    if (!sessionProceedModalLoading) {
      const summitId = selectedSummit?.id;
      // If quota value is defined, determine quota completion status
      if (summitId) {
        const hasAlreadyCompletedSummit = lastCompletedSummit === summitId;
        // If quota value is not defined, fall back to checking if the user has
        // completed each skill in the summit for the first time
        const justCompletedSummit =
          !hasAlreadyCompletedSummit && hasCompletedAllSkillsInSummit();
        if (justCompletedSummit) {
          dispatch(ActionCreators.setLastCompletedSummit(summitId));
        }
      }
    }
  }, [
    sessionProceedModalLoading,
    selfObject,
    selectedSummit,
    completedQuotaSessionsCount,
    summitSkillList,
    userSummitSessionList,
  ]);

  const _handleClearAllTimers = () => {
    clearChatFailureTimer();
    clearMatchReadyTimer();
    clearMatchProceedTimer();
    clearElapsedSecondsTimer();
  };

  const _handleSnapToSendKudos = useCallback(() => {
    // If not currently on a kudos card, proceed to kudos
    if (currentStepRef.current < kudosCardStep) {
      // we want to increment the current step one more time to denote that we're now on kudos
      // (since it's no longer incremented by match-step-proceed by this point)
      setCurrentStep(kudosCardStep);
      _handleDismissModal();
    }
  }, [skillObject]);

  const _handlePubNubMatchReady = useCallback(
    (remoteMessage: { user: string; skalesession_id: string }) => {
      if (remoteMessage["skalesession_id"] !== skaleSessionObject.id) {
        return;
      }
      if (remoteMessage.user !== selfObject.primaryEmail) {
        dispatch(ActionCreators.setIsPartnerChatReady(true));
      }
      UpduoAnalytics.logEvent("app-match-ready", {
        skalesessionId: remoteMessage.skalesession_id,
        user: remoteMessage.user,
      });
    },
    [dispatch, skaleSessionObject.sessionId]
  );

  const _handlePubNubMatchCancel = useCallback(
    (remoteMessage: { skalesession_id: string }) => {
      if (remoteMessage["skalesession_id"] !== skaleSessionObject.id) {
        return;
      }
      if (cancelAlertVisible) {
        setStagedMatchCancel(true);
      } else {
        _handleMatchCancel(
          false,
          "_handlePubNubMatchCancel !cancelAlertVisible"
        );
      }
    },
    [cancelAlertVisible]
  );

  const _handlePubNubMatchStepPartnerReady = useCallback(
    (remoteMessage: { step: number; skalesession_id: string }) => {
      if (remoteMessage["skalesession_id"] !== skaleSessionObject.id) {
        return;
      }
      if (!selfIsReadyToProceed) {
        UpduoSounds.firstReady.play();
      }
      setPartnerIsReadyToProceed(true);
      UpduoAnalytics.logEvent("app-match-step-partner-ready", remoteMessage);
    },
    [selfIsReadyToProceed, skaleSessionObject.sessionId]
  );

  const _handleMatchStepProceed = useCallback(
    (remoteMessage: {
      step: number;
      skalesession_id: string;
      recovered?: boolean;
    }) => {
      if (remoteMessage["skalesession_id"] !== skaleSessionObject.id) {
        return;
      }
      if (remoteMessage.step >= (skillObject?.sessionSteps?.length || 0)) {
        return;
      }
      clearMatchProceedTimer();
      setPartnerIsReadyToProceed(false);
      setSelfIsReadyToProceed(false);
      setMatchStepProceedObject(remoteMessage);
    },
    [skillObject, skaleSessionObject.sessionId]
  );

  const _handleAgendaButtonPress = useCallback(() => {
    UpduoAnalytics.logEvent("user-initial-agenda-close");
    setBeginChatButtonDisabled(true);
    setBeginChatButtonText("Starting Video...");
    // Post to signal that the user is ready to chat with partner
    declareMatchReady({
      variables: {
        declareMatchReadyRequest: {
          skalesessionId: skaleSessionObject.id,
        },
      },
    });
  }, [matchObject, skaleSessionObject.id, dispatch]);

  const _handleChatIssuePress = useCallback(() => {
    if (!buttonsDisabled) {
      // If user is on the last card, chat issue button triggers a match end
      const lastCardStep =
        skillObject && skillObject.sessionSteps
          ? skillObject.sessionSteps.length
          : 4;
      if (currentStepRef.current >= lastCardStep) {
        _handleEndMatch();
        return;
      }
      UpduoAnalytics.logEvent("user-end-chat-modal-open");
      setEndChatModalVisible(true);
    }
  }, [buttonsDisabled]);

  const _handleSkillPress = useCallback(() => {
    if (!buttonsDisabled) {
      UpduoAnalytics.logEvent("user-skill-modal-open");
      setShowSkillModalVisible(true);
    }
  }, [buttonsDisabled]);

  const _handleEndChatModalDismiss = () => {
    _handleDismissModal();
    UpduoAnalytics.logEvent("user-end-chat-modal-back");
  };

  const _handleSubmitSendKudos = () => {
    setButtonsDisabled(true);
    sendKudosAnalytics("user-kudos-send");
    sendKudos({
      variables: {
        sendKudosRequest: {
          categoryFriendly: kudosSelectedValue.Friendly,
          categoryGoodConversation: kudosSelectedValue["Good conversation"],
          categoryHelpfulTips: kudosSelectedValue["Helpful tips"],
          categoryKnowledgeable: kudosSelectedValue.Knowledgeable,
          message: kudosMessageText,
          sessionId: skaleSessionObject.id,
        },
      },
    });
  };

  const _handleSessionFinish = () => {
    UpduoSounds.proceed.play();
    _handleCloseChat();
    _handleDismissModal();
    UpduoAnalytics.logEvent("user-event-finish");
    dispatch(ActionCreators.setDisplayedSummitView("SessionFinish"));
  };

  const _handleDismissModal = useCallback(() => {
    // When a modal is dismissed, *all* modals should be dismissed
    dispatch(ActionCreators.setChatAgendaModalVisible(false));
    setShowStepDetailModal(false);
    setEndChatModalVisible(false);
    setShowSkillModalVisible(false);
    setSessionProceedModalVisible(false);
    setShowTechnicalSupportModal(false);
    setIsChatSettingsModalVisible(false);
  }, [dispatch]);

  const _handleCloseChat = useCallback(async () => {
    if (!chatEnded) {
      setChatEnded(true);
      _handleClearAllTimers();
      clearPubNubSubscribedMessages([
        "announcement",
        "me-update",
        "summit-update",
      ]);
      await agoraClient.leave();
      agoraClient.removeAllListeners();
      if (tracksRef.current) {
        for (const track of tracksRef.current) {
          track.stop();
          track.close();
        }
      }
      if (
        selfObject.organizationDoAgoraForceTcp ||
        selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTozOTI="
      ) {
        agoraClient.stopProxyServer();
      }
    }
  }, [chatEnded, agoraClient, _handleClearAllTimers]);

  const _handleCancelChat = useCallback(async () => {
    await _handleCloseChat();
    homeResetState();
    history.push("/");
  }, [_handleCloseChat]);

  const _handleMatchCancel = useCallback(
    (isInitiated: boolean, info = "") => {
      dispatch(ActionCreators.setChatPartnerHasCancelled(isInitiated));
      _handleDismissModal();
      UpduoAnalytics.logEvent("app-cancel-chat", {
        from: "SummitRoute",
        info,
      });
      _handleCancelChat();
    },
    [_handleCancelChat, _handleDismissModal, dispatch]
  );

  useEffect(() => {
    if (!cancelAlertVisible && stagedMatchCancel) {
      _handleMatchCancel(false, "!cancelAlertVisible && stagedMatchCancel");
    }
  }, [cancelAlertVisible, _handleMatchCancel, stagedMatchCancel]);

  const _handleInitiateEarlyEndChat = useCallback(
    (modalType) => {
      setButtonsDisabled(true);
      UpduoAnalytics.logEvent("user-cancel-chat", {
        from: "SummitRoute",
        function: `_handleInitiateEarlyEndChat ${modalType}`,
      });
      if (skaleSessionObject.id) {
        // Post a match cancellation request to the server
        declareMatchCancel({
          variables: {
            declareMatchCancelRequest: {
              skalesessionId: skaleSessionObject.id,
              duration: elapsedSecondsRef.current,
            },
          },
        });
      }
    },
    [_handleCloseChat, _handleMatchCancel, skaleSessionObject.id]
  );

  const _handleAgendaBackPress = useCallback(() => {
    if (!buttonsDisabled && !cancelAlertVisible) {
      UpduoAnalytics.logEvent("user-initial-agenda-back");
      setCancelAlertVisible(true);
    }
    return true;
  }, [buttonsDisabled, cancelAlertVisible, _handleInitiateEarlyEndChat]);

  const _handleShowSkillDismiss = () => {
    _handleDismissModal();
    UpduoAnalytics.logEvent("user-show-skill-modal-back");
  };

  const _handleOnPressChatSettings = () => {
    UpduoAnalytics.logEvent("user-chat-press-chat-settings");
    setIsTroubleshootingMenuVisible(false);
    setIsChatSettingsModalVisible(true);
    setShowTechnicalSupportModal(false);
    setShowIssueModal(false);
  };

  const _handleOnPressTechnicalSupport = () => {
    UpduoAnalytics.logEvent("user-chat-press-technical-support");
    setIsTroubleshootingMenuVisible(false);
    setIsChatSettingsModalVisible(false);
    setShowTechnicalSupportModal(true);
    setShowIssueModal(false);
  };

  const _handleOnPressIssue = () => {
    UpduoAnalytics.logEvent("user-chat-press-issue");
    setIsTroubleshootingMenuVisible(false);
    setIsChatSettingsModalVisible(false);
    setShowTechnicalSupportModal(false);
    setShowIssueModal(true);
  };

  const _handleAgoraGraceExpired = useCallback(() => {
    // Only declare failure if fail listeners are enabled,
    // user is on Chat screen, and rating modal isn't visible
    const declareFailure = displayedSummitView === "Chat";
    if (declareFailure) {
      UpduoAnalytics.logEvent("app-chat-failed", {
        cause: "onNeverConnectedAgora",
      });
      dispatch(ActionCreators.setChatFailed(chatAgendaModalVisible));
      _handleCancelChat();
      declareMatchCancel({
        variables: {
          declareMatchCancelRequest: {
            skalesessionId: matchObject.skalesessionId,
            cancelledReason: CancelReasonEnum.ConnectionIssues,
          },
        },
      });
    }
  }, [displayedSummitView, chatAgendaModalVisible]);

  const _handleShowSessionProceedModal = () => {
    getUserSummitSessionList({
      variables: {
        getUserSummitSessionListRequest: {
          summitId: selectedSummit?.id || skaleSessionObject?.summitId,
        },
      },
    });
    dispatch(ActionCreators.setIsPostEvent(true));
    setSessionProceedModalVisible(true);
    _handleCloseChat();
  };

  useEffect(() => {
    if (!publishVideo) {
      _handleCloseChat();
    }
  }, [publishVideo, _handleCloseChat]);

  const _handleEndMatch = useCallback(() => {
    _handleDismissModal();
    _handleCloseChat();
    setStepElapsedSeconds(0);
    setPublishVideo(false);
    declareMatchEnd({
      variables: {
        declareMatchEndRequest: {
          skalesessionId: skaleSessionObject.id,
          duration: elapsedSecondsRef.current,
        },
      },
    });
    UpduoAnalytics.logEvent("app-end-chat");
  }, [
    _handleCloseChat,
    _handleDismissModal,
    _handleShowSessionProceedModal,
    dispatch,
    skaleSessionObject.id,
  ]);

  const _handlePrepareForChat = useCallback(() => {
    if (!skillObject.name) {
      _handleCancelChat();
      return;
    }
    // Remove PubNub events left over from Home screen
    clearPubNubSubscribedMessages([
      "announcement",
      "me-update",
      "summit-update",
    ]);

    // add PubNub subscribed messages
    addPubNubSubscribedMessage(
      "match-ready",
      (remoteMessage: { user: string; skalesession_id: string }) => {
        _handlePubNubMatchReady(remoteMessage);
      }
    );

    addPubNubSubscribedMessage(
      "match-cancel",
      (remoteMessage: { skalesession_id: string }) => {
        _handlePubNubMatchCancel(remoteMessage);
      }
    );

    addPubNubSubscribedMessage(
      "match-step-partner-ready",
      (remoteMessage: { step: number; skalesession_id: string }) => {
        _handlePubNubMatchStepPartnerReady(remoteMessage);
      }
    );

    addPubNubSubscribedMessage(
      "match-step-proceed",
      (remoteMessage: { step: number; skalesession_id: string }) => {
        _handleMatchStepProceed(remoteMessage);
      }
    );

    addPubNubSubscribedMessage(
      "match-end",
      (remoteMessage: { skalesession_id: string; is_cancelled: boolean }) => {
        _handlePubNubMatchEnd(remoteMessage);
      }
    );
  }, [
    skillObject,
    _handlePubNubMatchCancel,
    _handlePubNubMatchReady,
    _handlePubNubMatchStepPartnerReady,
    _handleMatchStepProceed,
    _handleCancelChat,
  ]);

  const _handlePubNubMatchEnd = useCallback(
    (remoteMessage: { skalesession_id: string; is_cancelled: boolean }) => {
      if (remoteMessage["skalesession_id"] === skaleSessionObject.id) {
        setSessionSuccess({
          skalesessionId: remoteMessage["skalesession_id"],
          isCancelled: remoteMessage["is_cancelled"],
        });
        if (!chatEnded) {
          _handleEndMatch();
        }
      }
      manuallyInvalidateQueries("userSessions");
    },
    [chatEnded]
  );

  const _handleReadyButtonPress = useCallback(() => {
    // Post to signal that the user is ready for next step
    // End the video call if user is on last official step
    const lastCardStep =
      skillObject && skillObject.sessionSteps
        ? skillObject.sessionSteps.length
        : 4;
    if (currentStepRef.current >= lastCardStep) {
      setShowLeaveButton(false);
      _handleEndMatch();
      return;
    }
    setSelfIsReadyToProceed(true);
    // Post page index on video chat for synchronizing swipes
    declareMatchStepReady({
      variables: {
        declareMatchStepReadyRequest: {
          skalesessionId: skaleSessionObject.id,
          step: currentStepRef.current,
          elapsedSeconds: elapsedSecondsRef.current,
          stepElapsedSeconds: stepElapsedSecondsRef.current,
        },
      },
    });
    if (!partnerIsReadyToProceed) {
      UpduoSounds.firstReady.play();
    }
  }, [partnerIsReadyToProceed, skillObject, skaleSessionObject.sessionId]);

  const kudosData = {
    isKudosCard: true,
    submitSendKudos: true,
  };

  let allStepCardData = skillObject?.sessionSteps || [];
  // Last card's ready button should start sending kudos
  if (allStepCardData.length) {
    allStepCardData[allStepCardData.length - 1].doesEndVideoCall = true;
  }
  allStepCardData = allStepCardData.concat(kudosData);

  const isRecording = selfObject.organizationArchiveRatio > 0;
  const recommendedMinutes =
    allStepCardData[currentStepRef.current - 1]["minutes"];
  const recommendedSeconds = recommendedMinutes * 60;
  const overTime = stepElapsedSeconds - recommendedSeconds;
  let timerTextColor = skaleWhite;
  let timerBorderColor = "transparent";
  let timerBackgroundColor = colorWithOpacity(skaleBackgroundWhite, 0.37);
  let timerBorderWidth = 0;
  if (overTime > 0) {
    timerBorderWidth = 2;
    switch (Math.floor(overTime / 30)) {
      case 0:
        timerBorderColor = skaleGreen;
        timerBackgroundColor = colorWithOpacity(skaleGreen, 0.2);
        break;
      case 1:
        timerBorderColor = skaleOrange;
        timerBackgroundColor = colorWithOpacity(skaleOrange, 0.1);
        break;
      default:
        timerTextColor = skaleSoftRed;
        timerBorderColor = skaleSoftRed;
        timerBackgroundColor = colorWithOpacity(skaleSoftRed, 0.2);
        break;
    }
  }
  return (
    <View
      style={{
        backgroundColor: skaleOffWhite,
        height: "calc(100vh - 3rem)", // hacky sln to remove bottom margin automatically added to each page
      }}
    >
      <View
        style={{
          flexDirection: "row",
          marginHorizontal: 50,
          marginTop: 50,
        }}
      >
        <View style={{ flex: 7 }}>
          <div
            className={"subscriberContainer chat"}
            style={{ backgroundColor: skaleBlack }}
          >
            {agoraRemoteUsers?.length > 0 &&
            agoraRemoteUsers[0]?.videoTrack &&
            !chatAgendaModalVisible ? (
              <AgoraVideoPlayer
                videoTrack={agoraRemoteUsers[0].videoTrack}
                style={{ height: "100%", width: "100%" }}
              />
            ) : null}
          </div>
          <View
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              justifyContent: "flex-end",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 22,
              }}
            >
              <FadeInOutView
                duration={500}
                style={{
                  minWidth: 54,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  backgroundColor: timerBackgroundColor,
                  borderRadius: 6,
                  justifyContent: "center",
                  alignItems: "center",
                  borderColor: timerBorderColor,
                  borderWidth: timerBorderWidth,
                }}
              >
                <Text
                  isHeading={true}
                  style={{ fontSize: 15, color: timerTextColor }}
                >
                  {formattedTimeString(stepElapsedSeconds)}
                </Text>
              </FadeInOutView>
            </View>
          </View>
        </View>
        <View
          style={{
            flex: 3,
            marginLeft: 30,
          }}
        >
          <div
            className={"publisherContainer chat"}
            // Move self view when window height < 750
            style={
              Dimensions.get("window").width < 1000 ||
              Dimensions.get("window").height < 750
                ? {
                    backgroundColor: skaleBlack,
                    position: "absolute",
                    top: 40,
                    right: Dimensions.get("window").width * 0.3 + 30,
                    width: 150,
                    height: 150,
                  }
                : {
                    backgroundColor: skaleBlack,
                    marginBottom: 30,
                  }
            }
          >
            {readyRef.current && tracksRef.current ? (
              <AgoraVideoPlayer
                videoTrack={tracksRef.current[1]}
                style={{ height: "100%", width: "100%", zIndex: 2000 }}
              />
            ) : null}
          </div>
          <View style={{ flex: 1 }}>
            <SwipeableViews
              disabled={true}
              index={currentStep - 1}
              containerStyle={{ height: "100%" }}
              style={{ height: "100%" }}
            >
              {allStepCardData.map((item, index: number) => {
                return (
                  <StepCard
                    key={index}
                    heading={item[isLearner ? "learner" : "teacher"]?.heading}
                    recommendedMinutes={item.minutes}
                    totalSteps={skillObject?.sessionSteps?.length || 0}
                    currentStepIndex={index}
                    isKudosCard={item.isKudosCard}
                    showDetailButton={
                      item[isLearner ? "learner" : "teacher"]?.detail
                    }
                    partnerIsReadyToProceed={partnerIsReadyToProceed}
                    partnerName={partnerObject.firstName}
                    submitSendKudos={item.submitSendKudos}
                    doesEndVideoCall={item.doesEndVideoCall}
                    onSubmitSendKudosButtonPress={_handleSubmitSendKudos}
                    onKudosSkipPress={() => {
                      sendKudosAnalytics("user-kudos-skip");
                      _handleShowSessionProceedModal();
                    }}
                    onReadyButtonPress={_handleReadyButtonPress}
                    onDetailPress={() => {
                      UpduoAnalytics.logEvent("user-step-detail", {
                        step: currentStepRef.current,
                        skalesessionId: matchObject.skalesessionId,
                        stepElapsedSeconds,
                        convoId: skillObject.id,
                        convoName: skillObject.name,
                      });
                      setShowStepDetailModal(true);
                    }}
                    kudosSelectedValue={kudosSelectedValue}
                    onKudosSelectedValueChange={(element) => {
                      const value = kudosSelectedValue[`${element}`];
                      const newObject = { [element]: !value };
                      setKudosSelectedValue((ele) => ({
                        ...ele,
                        ...newObject,
                      }));
                    }}
                    onKudosMessageChange={setKudosMessageText}
                    selfIsReadyToProceed={selfIsReadyToProceed}
                    buttonsDisabled={
                      buttonsDisabled ||
                      (item.isKudosCard &&
                        !(
                          // Kudos must have at least one category and 4 characters long
                          Object.values(kudosSelectedValue).some((element) => {
                            return element;
                          })
                        ))
                    }
                    delayButton={delayButton}
                    overTime={overTime}
                  />
                );
              })}
            </SwipeableViews>
          </View>
        </View>
      </View>
      <View
        style={{
          flexDirection: "row",
          paddingHorizontal: 30,
          justifyContent: "space-between",
          alignItems: "center",
          height: 100,
          position: "absolute",
          paddingVertical: 22,
          bottom: -48,
          left: 0,
          right: 0,
          backgroundColor: skaleWhite,
        }}
      >
        <View style={{ flexDirection: "row" }}>
          <View style={{ flex: 1, marginRight: 15 }}>
            <TouchableOpacity onPress={() => _handleSkillPress()}>
              <View style={{ flexDirection: "row" }}>
                <View
                  style={{
                    flexDirection: "column",
                    maxWidth: 600,
                    flexShrink: 1,
                  }}
                >
                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Text
                      isHeading={true}
                      style={{
                        fontSize: 20,
                      }}
                      numberOfLines={1}
                    >
                      {skillObject.name}
                    </Text>
                    <Text
                      style={{
                        paddingLeft: 10,
                        alignSelf: "center",
                        color: skalePrimaryBlue,
                      }}
                    >
                      <Icon size={20} icon={ic_info_outline} />
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontSize: 14,
                      color: colorWithOpacity(skaleBlack, 0.6),
                    }}
                    numberOfLines={1}
                  >
                    {`${selfObject.organizationName}, ${partnerObject.firstName} ${partnerObject.lastName}`}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </View>
        <View
          style={{
            flexDirection: "row",
            marginLeft: 30,
            marginRight: 70,
            justifyContent: "flex-end",
          }}
        >
          {showLeaveButton ? (
            <Button
              width={80}
              height={30}
              onPress={() => {
                _handleChatIssuePress();
              }}
              text={"LEAVE"}
              textColor={skalePrimaryBlue}
              fontSize={14}
              color={skaleWhite}
              borderColor={skaleBackgroundBlue}
              borderWidth={1}
              borderRadius={4}
            />
          ) : null}
          <View>
            <TouchableOpacity
              onPress={() => setIsTroubleshootingMenuVisible((value) => !value)}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                marginLeft: 16,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: skalePrimaryBlue,
              }}
            >
              <Text
                style={{
                  alignSelf: "center",
                  justifyContent: "center",
                  color: skaleWhite,
                  marginBottom: 3,
                }}
              >
                <Icon size={20} icon={settings} />
              </Text>
            </TouchableOpacity>
            {isTroubleshootingMenuVisible ? (
              <View
                style={{
                  position: "absolute",
                  bottom: 60,
                  right: 0,
                  zIndex: 100,
                  backgroundColor: skaleWhite,
                  width: 200,
                  borderRadius: 8,
                  elevation: 5,
                  shadowRadius: 2,
                  shadowColor: colorWithOpacity(skaleBlack, 0.8),
                  shadowOpacity: 0.3,
                  shadowOffset: {
                    width: 1,
                    height: 1,
                  },
                }}
              >
                <TouchableOpacity
                  onPress={_handleOnPressChatSettings}
                  style={{
                    flex: 1,
                    padding: 10,
                    justifyContent: "center",
                    borderBottomWidth: 1,
                    borderBottomColor: skaleLightGray,
                  }}
                >
                  <Text>{"Audio/Video Settings"}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={_handleOnPressTechnicalSupport}
                  style={{
                    flex: 1,
                    padding: 10,
                    justifyContent: "center",
                    borderBottomWidth: 1,
                    borderBottomColor: skaleLightGray,
                  }}
                >
                  <Text>{"Troubleshooting"}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={_handleOnPressIssue}
                  style={{
                    flex: 1,
                    padding: 10,
                    justifyContent: "center",
                  }}
                >
                  <Text>{"Report a Problem"}</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </View>
      </View>
      {partnerDisconnectedAlertVisible ? (
        <FadeInOutView
          visible={partnerDisconnectedAlertVisible && publishVideo}
          style={{
            position: "absolute",
            top: 80,
            left: 80,
            alignItems: "center",
          }}
        >
          <Text
            style={{ color: skaleWhite, marginBottom: 20, textAlign: "center" }}
          >
            {
              "Your partner lost connection, but may come back.\nYou may either leave or continue waiting."
            }
          </Text>
          <Button
            onPress={() => _handleInitiateEarlyEndChat("MainView")}
            disabled={buttonsDisabled}
            text={"Leave"}
          />
        </FadeInOutView>
      ) : (
        <FadeInOutView
          duration={100}
          visible={videoDisabledAlertVisible && publishVideo}
          style={{
            position: "absolute",
            top: 80,
            left: 80,
          }}
        >
          <Text style={{ color: skaleWhite, textAlign: "center" }}>
            {"Low Bandwidth Mode"}
          </Text>
        </FadeInOutView>
      )}
      <MainOverlay
        showRecordingDot={isRecording && !chatAgendaModalVisible}
        showButtons={true}
      />
      <AgendaModal
        onClose={_handleAgendaBackPress}
        buttonDisabled={beginChatButtonDisabled}
        cancelAlertVisible={cancelAlertVisible}
        onButtonPress={_handleAgendaButtonPress}
        buttonText={beginChatButtonText}
        communityName={selfObject.organizationName}
        isRecording={isRecording}
        audioTrack={tracksRef.current ? tracksRef.current[0] : null}
        videoTrack={tracksRef.current ? tracksRef.current[1] : null}
      />
      <AgendaBackModal
        onDismiss={() => {
          setCancelAlertVisible(false);
        }}
        onAgendaBackConfirm={() => {
          _handleInitiateEarlyEndChat("AgendaModal");
        }}
        isVisible={cancelAlertVisible}
      />
      <EndChatModal
        onDismiss={() => _handleEndChatModalDismiss()}
        onCancelChatConfirm={() => _handleInitiateEarlyEndChat("EndChatModal")}
        isVisible={endChatModalVisible}
      />
      <SkillModal
        onDismiss={() => _handleShowSkillDismiss()}
        isVisible={showSkillModalVisible}
        skill={skillObject}
        summitSkillList={summitSkillList}
        userId={selfObject.userId}
        userSummitSessionList={userSummitSessionList}
        communityName={selfObject.organizationName}
      />

      <SessionProceedModal
        skillName={skillObject.name}
        onSessionFinish={_handleSessionFinish}
        completedSessionsCount={completedQuotaSessionsCount || undefined}
        sessionQuotaCount={
          selfObject.organizationActiveQuota?.requiredSessionCount
        }
        isVisible={sessionProceedModalVisible}
        isLoading={sessionProceedModalLoading}
        lastSessionWasCancelled={
          (sessionSuccess &&
            sessionSuccess.skalesessionId === skaleSessionObject.id &&
            sessionSuccess.isCancelled) ||
          false
        }
      />

      <StepDetailModal
        isVisible={showStepDetailModal}
        stepDetail={
          currentStepRef.current <= (skillObject?.sessionSteps?.length || 0)
            ? allStepCardData[currentStepRef.current - 1][
                isLearner ? "learner" : "teacher"
              ].detail
            : ""
        }
        onDismiss={() => {
          UpduoAnalytics.logEvent("user-step-detail-dismiss", {
            step: currentStepRef.current,
            skalesessionId: matchObject.skalesessionId,
            stepElapsedSeconds,
            convoId: skillObject.id,
            convoName: skillObject.name,
          });
          _handleDismissModal();
        }}
      />
      <TechnicalSupportModal
        isVisible={showTechnicalSupportModal}
        onClose={() => {
          setShowTechnicalSupportModal(false);
        }}
      />
      <ChatSettingsModal
        isVisible={isChatSettingsModalVisible}
        onClose={() => {
          setIsChatSettingsModalVisible(false);
        }}
        audioTrack={tracksRef.current ? tracksRef.current[0] : null}
        videoTrack={tracksRef.current ? tracksRef.current[1] : null}
      />
      <IssueModal
        sessionId={skaleSessionObject?.id}
        isVisible={showIssueModal}
        onDismiss={() => {
          setShowIssueModal(false);
        }}
      />
    </View>
  );
}
